import React, { useEffect, useState, useRef } from 'react';

export interface TenantLogoProps {
  logoUrl?: string;
  fallback: string;
  alt?: string;
  sx?: React.CSSProperties;
}

const logoBase64Cache: { [url: string]: string } = {};

const TenantLogo: React.FC<TenantLogoProps> = ({ logoUrl, fallback, alt = 'Logo', sx }) => {
  const [logoBase64, setLogoBase64] = useState<string | null>(() => {
    if (logoUrl && logoBase64Cache[logoUrl]) {
      return logoBase64Cache[logoUrl];
    }
    return null;
  });
  const currentUrlRef = useRef<string | undefined>(logoUrl);
  const isMountedRef = useRef(true);

  // Edge: fallback to force `<img onError>` for broken/corrupt images
  const [hasImgError, setHasImgError] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    currentUrlRef.current = logoUrl;
    setHasImgError(false); // Clear error state when URL changes

    if (!logoUrl) return;

    // If already cached, use it immediately
    if (logoBase64Cache[logoUrl]) {
      setLogoBase64(logoBase64Cache[logoUrl]);
      return;
    }
    const abortController = new AbortController();

    async function fetchLogoAsBase64(url: string) {
      try {
        const response = await fetch(url, { signal: abortController.signal });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (
            currentUrlRef.current === url &&
            isMountedRef.current &&
            typeof reader.result === 'string'
          ) {
            logoBase64Cache[url] = reader.result;
            setLogoBase64(reader.result);
          }
        };
        reader.onerror = () => {
          // No state update on error
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        // Only log if not aborted
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          // eslint-disable-next-line no-console
          console.warn('Failed to load logo:', err);
        }
      }
    }

    fetchLogoAsBase64(logoUrl);

    return () => {
      isMountedRef.current = false;
      abortController.abort();
    };
  }, [logoUrl]);

  // Use base64/cached logo, otherwise fallback, and allow <img onError> to catch base64 corruption too
  const imgSrc = !logoUrl || hasImgError ? fallback : (logoBase64 || logoUrl);

  const handleError = () => {
    if (!hasImgError) setHasImgError(true);
  };

  return (
    <img
      src={imgSrc}
      alt={alt || 'Tenant logo'}
      style={sx}
      loading="lazy"
      onError={handleError}
    />
  );
};

export default TenantLogo;